import { EmailInboxListResponseSchema, EmailInboxDetailSchema, ConnectEmailInboxAuthorizeResponseSchema, DisconnectEmailInboxResponseSchema, FetchEmailsResponseSchema, StoredEmailsResponseSchema, StoredEmailsAggregateResponseSchema, StoredEmailDetailSchema, StoredEmailAttachmentsResponseSchema, ManualEmailMatchResponseSchema, EmailInboxFoldersResponseSchema, } from '@packages/types';
import { api, apiRaw } from '../client.js';
export function listEmailInboxes(accountId, params) {
    const query = new URLSearchParams();
    if (params?.cursor)
        query.append('cursor', params.cursor);
    if (params?.limit !== undefined)
        query.append('limit', params.limit.toString());
    const qs = query.toString();
    return api(qs ? `/accounts/${accountId}/email-inboxes?${qs}` : `/accounts/${accountId}/email-inboxes`, EmailInboxListResponseSchema);
}
/**
 * List the synced folders (inbox, label, or mailbox) across the email inboxes
 * the caller can see. Pass `emailInboxId` to narrow to one inbox. Regular
 * members see only folders of inboxes they personally connected; assistants
 * see every inbox in the account (enforced server-side).
 */
export function getEmailInboxFolders(accountId, params) {
    const query = new URLSearchParams();
    if (params?.emailInboxId !== undefined)
        query.append('emailInboxId', params.emailInboxId);
    if (params?.cursor !== undefined)
        query.append('cursor', params.cursor);
    if (params?.limit !== undefined)
        query.append('limit', params.limit.toString());
    const qs = query.toString();
    return api(qs ? `/accounts/${accountId}/email-inbox-folders?${qs}` : `/accounts/${accountId}/email-inbox-folders`, EmailInboxFoldersResponseSchema);
}
export function getEmailInboxAuthUrl(accountId, provider, frontendUrl) {
    return api(`/accounts/${accountId}/email-inboxes/connect/${provider}/authorize?frontendUrl=${encodeURIComponent(frontendUrl)}`, ConnectEmailInboxAuthorizeResponseSchema);
}
/** Only the id is read back: a chat-started connect reports it as the connection that now exists. */
const ConnectedEmailInboxSchema = EmailInboxDetailSchema.pick({ id: true });
export function handleEmailInboxCallback(accountId, provider, body) {
    return api(`/accounts/${accountId}/email-inboxes/connect/${provider}/callback`, ConnectedEmailInboxSchema, {
        method: 'POST',
        body: {
            code: body.code,
            state: body.state,
            ...(body.initialSyncDays !== undefined && { initialSyncDays: body.initialSyncDays }),
        },
    });
}
export function disconnectEmailInbox(accountId, inboxId) {
    return api(`/accounts/${accountId}/email-inboxes/${inboxId}`, DisconnectEmailInboxResponseSchema, {
        method: 'DELETE',
    });
}
export function fetchEmails(accountId, emailAddress, limit) {
    const params = new URLSearchParams({ emailAddress });
    if (limit) {
        params.append('limit', limit.toString());
    }
    return api(`/accounts/${accountId}/email-inboxes/emails?${params.toString()}`, FetchEmailsResponseSchema);
}
function buildStoredEmailsQueryString(options) {
    const params = new URLSearchParams();
    if (options?.limit)
        params.append('limit', options.limit.toString());
    if (options?.cursor)
        params.append('cursor', options.cursor);
    if (options?.ids !== undefined) {
        for (const id of options.ids)
            params.append('ids', id);
    }
    if (options?.direction !== undefined)
        params.append('direction', options.direction);
    if (options?.dateFrom !== undefined)
        params.append('dateFrom', options.dateFrom);
    if (options?.dateTo !== undefined)
        params.append('dateTo', options.dateTo);
    if (options?.emailInboxId !== undefined)
        params.append('emailInboxId', options.emailInboxId);
    if (options?.clientId !== undefined)
        params.append('clientId', options.clientId);
    if (options?.clientProjectId !== undefined)
        params.append('clientProjectId', options.clientProjectId);
    if (options?.isRead !== undefined) {
        // BooleanQueryParamSchema on the backend expects the string 'true' or 'false', not a native boolean
        params.append('isRead', options.isRead ? 'true' : 'false');
    }
    if (options?.processingStatus !== undefined)
        params.append('processingStatus', options.processingStatus);
    if (options?.matchState !== undefined)
        params.append('matchState', options.matchState);
    if (options?.hasAttachment !== undefined) {
        // BooleanQueryParamSchema on the backend expects the string 'true' or 'false', not a native boolean
        params.append('hasAttachment', options.hasAttachment ? 'true' : 'false');
    }
    if (options?.ignoredState !== undefined)
        params.append('ignoredState', options.ignoredState);
    if (options?.folderId !== undefined)
        params.append('folderId', options.folderId);
    if (options?.deletedState !== undefined)
        params.append('deletedState', options.deletedState);
    if (options?.proposalState !== undefined)
        params.append('proposalState', options.proposalState);
    if (options?.search !== undefined)
        params.append('search', options.search);
    if (options?.sortBy !== undefined)
        params.append('sortBy', options.sortBy);
    if (options?.sortDirection !== undefined)
        params.append('sortDirection', options.sortDirection);
    return params.toString();
}
export function getStoredEmails(accountId, emailInboxId, options) {
    const qs = buildStoredEmailsQueryString(options);
    return api(`/accounts/${accountId}/email-inboxes/${emailInboxId}/emails${qs ? `?${qs}` : ''}`, StoredEmailsResponseSchema);
}
/**
 * List stored emails across every inbox the caller can see, with one globally
 * sorted cursor. Server-side aggregation - replaces a per-inbox client-side
 * merge that produced out-of-order pages on multi-inbox accounts.
 *
 * Visibility rule (enforced server-side): regular members see only emails from
 * inboxes they personally connected; assistants see all of the account's inboxes.
 */
export function getAllStoredEmails(accountId, options) {
    const qs = buildStoredEmailsQueryString(options);
    return api(`/accounts/${accountId}/stored-emails${qs ? `?${qs}` : ''}`, StoredEmailsResponseSchema);
}
/**
 * Count stored emails over the same filters and per-member inbox visibility as
 * `getAllStoredEmails`. The route rejects paging and sort params, so only the
 * filter options are accepted here.
 */
export function aggregateAllStoredEmails(accountId, options) {
    const { groupBy, ...filters } = options ?? {};
    const params = new URLSearchParams(buildStoredEmailsQueryString(filters));
    if (groupBy)
        params.set('groupBy', groupBy);
    const qs = params.toString();
    return api(`/accounts/${accountId}/stored-emails/aggregate${qs ? `?${qs}` : ''}`, StoredEmailsAggregateResponseSchema);
}
export function getStoredEmail(accountId, storedEmailId) {
    return api(`/accounts/${accountId}/stored-emails/${storedEmailId}`, StoredEmailDetailSchema);
}
/**
 * Fetch attachment metadata for a single stored email. Lazy-loaded by the UI
 * when the user expands an email row – omitted from the main email list payload
 * for data minimisation (filenames are only decrypted on demand).
 *
 * Returns `{ attachments: [] }` when the email has no attachments.
 */
export function getStoredEmailAttachments(accountId, storedEmailId) {
    return api(`/accounts/${accountId}/stored-emails/${storedEmailId}/attachments`, StoredEmailAttachmentsResponseSchema);
}
export function createManualEmailMatch(accountId, storedEmailId, body) {
    return api(`/accounts/${accountId}/stored-emails/${storedEmailId}/matches`, ManualEmailMatchResponseSchema, {
        method: 'POST',
        body,
    });
}
export async function deleteManualEmailMatch(accountId, storedEmailId, matchId) {
    await apiRaw(`/accounts/${accountId}/stored-emails/${storedEmailId}/matches/${matchId}`, { method: 'DELETE' });
}
/**
 * Promote a purely-associative match (filtered / sub-threshold) to create a
 * draft time entry. Idempotent on the server.
 */
export async function promoteEmailMatch(accountId, storedEmailId, matchId) {
    await apiRaw(`/accounts/${accountId}/stored-emails/${storedEmailId}/matches/${matchId}/promote`, { method: 'POST' });
}
/**
 * Confirm a live proposal, promoting it to the email's chosen allocation. The
 * `matchId` names the exact proposal the user saw - a parameterless "confirm
 * whatever is live" call could promote a different candidate written between
 * render and click, recording a client the user never agreed to.
 *
 * The route returns 409 `PROPOSAL_SUPERSEDED` when a different proposal
 * is now live and 409 `ALREADY_ALLOCATED` when the email was allocated
 * (to any match) since the proposal was read; both are ApiError, not a thrown
 * plain Error, so callers branch on `err.code`.
 *
 * `options.billable` carries the person's answer to "is there billable time
 * here" when they overrode the prediction; leave it out to follow the
 * prediction (see `ConfirmProposalInputSchema`).
 */
export async function confirmEmailProposal(accountId, storedEmailId, matchId, options = {}) {
    await apiRaw(`/accounts/${accountId}/stored-emails/${storedEmailId}/proposal/confirm`, { method: 'POST', body: { matchId, ...options } });
    return { success: true };
}
/**
 * Dismiss a live proposal. Soft-hides it (the row's `dismissedAt` never
 * clears); the email stays unallocated and offerable for a different match.
 * Same `matchId` contract as {@link confirmEmailProposal}.
 */
export async function dismissEmailProposal(accountId, storedEmailId, matchId) {
    await apiRaw(`/accounts/${accountId}/stored-emails/${storedEmailId}/proposal/dismiss`, { method: 'POST', body: { matchId } });
    return { success: true };
}
/**
 * Mark a stored email as non-billable. Idempotent on the server.
 *
 * By default (no `train` option or `train: true`) also adds the counterparty
 * address to the per-user allowlist so future emails from the same sender are
 * auto-marked. Pass `{ train: false }` to mark this one email without training
 * the allowlist ("ignore once" semantics).
 */
export async function ignoreEmail(accountId, storedEmailId, options) {
    const params = new URLSearchParams();
    if (options?.train !== undefined) {
        params.append('train', options.train ? 'true' : 'false');
    }
    const qs = params.toString();
    await apiRaw(`/accounts/${accountId}/stored-emails/${storedEmailId}/mark-non-billable${qs ? `?${qs}` : ''}`, { method: 'POST' });
}
/**
 * Undo a non-billable mark. Idempotent on the server.
 *
 * By default (no `untrain` option or `untrain: true`) also removes the
 * counterparty address from the per-user allowlist so the next inbound email
 * from the same address is not auto-marked. Pass `{ untrain: false }` to clear
 * only this one email's mark while leaving the standing rule in place ("rescue
 * once" semantics).
 */
export async function unignoreEmail(accountId, storedEmailId, options) {
    const params = new URLSearchParams();
    if (options?.untrain !== undefined) {
        params.append('untrain', options.untrain ? 'true' : 'false');
    }
    const qs = params.toString();
    await apiRaw(`/accounts/${accountId}/stored-emails/${storedEmailId}/mark-non-billable${qs ? `?${qs}` : ''}`, { method: 'DELETE' });
}
/**
 * Add or remove the per-user ignore rule for a stored email's counterparty
 * address(es) without touching the email's ignored state.
 *
 * `active: true` upserts the rule (future emails from this address are auto-
 * marked). `active: false` removes it. Idempotent on the server.
 */
export async function setEmailIgnoreRule(accountId, storedEmailId, active) {
    await apiRaw(`/accounts/${accountId}/stored-emails/${storedEmailId}/ignore-rule`, { method: 'PUT', body: { active } });
}
/**
 * Download the raw bytes of an email attachment on demand.
 *
 * Fetches the bytes live from the provider (pure pass-through – nothing is
 * cached or persisted). The bearer token is forwarded automatically via
 * `apiRaw`, so this is safe to call without any extra auth headers.
 *
 * The server sets `Content-Type` to the stored mimeType and
 * `Content-Disposition: attachment; filename=<decrypted-name>`, so the
 * browser download dialog will show the correct filename.
 *
 * Throws an `ApiError` when:
 * - The attachment or its parent email is not found, or belongs to another
 *   account (404 / `NOT_FOUND`).
 * - The provider reports the message or attachment is permanently gone (404).
 * - The stored size exceeds the server-side ceiling (400 / `BAD_REQUEST`).
 * - A transient provider error occurs (502 / `UPSTREAM_ERROR`).
 */
export async function downloadStoredEmailAttachment(accountId, storedEmailId, attachmentId) {
    const response = await apiRaw(`/accounts/${accountId}/stored-emails/${storedEmailId}/attachments/${attachmentId}/content`);
    return response.blob();
}
/**
 * Connect a generic IMAP inbox to an account using direct server credentials.
 *
 * Validates the connection before persisting. On failure, the server returns a
 * 400 with `context.imapErrorCode` set to one of:
 * - `unreachable`: cannot reach the server (check host/port)
 * - `tls_handshake_failed`: TLS negotiation failed (try a different TLS mode)
 * - `auth_failed`: username or password rejected
 * - `imap_not_supported`: server does not speak IMAP4rev1
 * - `select_inbox_failed`: connected but could not open the INBOX folder
 *
 * On conflict (the same email address is already connected as an active inbox),
 * returns 409 with code `CONFLICT`.
 *
 * `initialSyncDays` is optional: omit or pass null to use the system default
 * (10 most recent emails); pass 0 to skip historical sync entirely.
 */
export function connectImapEmailInbox(accountId, body) {
    return api(`/accounts/${accountId}/email-inboxes/connect/imap`, EmailInboxDetailSchema, {
        method: 'POST',
        body,
    });
}
